import { Search } from '@mui/icons-material';
import { Box, Button, InputAdornment, TableSortLabel, TextField, Typography } from '@mui/material';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useLabs } from 'context/labs-context';
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import FreeTextFilterComponent from 'components/users/shared/free-text-filter/free-text-filter.component';

type ConnectLabsDialogProps = {
    open: boolean;
    onClose: () => void;
};

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'button', width: 80 },
];

const ConnectLabsDialog: FC<ConnectLabsDialogProps> = ({ open, onClose }) => {
    const { t: translate } = useTranslation(['common', 'organization', 'invoices']);

    const {
        allLabs,
        isLoadingAllLabs,
        fetchAllLabs,
        connectToLab,
        isConnectingLab,
        isAnyLabConnecting,
        allLabsSortOrder,
        setAllLabsSortOrder,
        allLabsSearchText,
        setAllLabsSearchText,
    } = useLabs();

    const { setAppToasterContent } = useContext(AppToasterContext);

    // Fetch labs
    useEffect(() => {
        if (open) {
            fetchAllLabs(allLabsSearchText);
        }
    }, [open]);

    const handleFreeTextChange = (freeTextInput: string) => {
        setAllLabsSearchText(freeTextInput);
    };

    const handleConnect = async (labId: string) => {
        try {
            await connectToLab(labId);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('organization:LAB_CONNECTED_SUCCESSFULLY'),
            });
        } catch {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('common:ERROR_OCCURRED'),
            });
        }
    };

    const handleSort = () => {
        const newOrder = allLabsSortOrder === 'asc' ? 'desc' : 'asc';
        setAllLabsSortOrder(newOrder);
    };

    // Row renderer - memoized
    const Row = useCallback(
        ({ index, style, data }: ListChildComponentProps) => {
            if (isLoadingAllLabs) {
                return (
                    <Box
                        style={style}
                        display="flex"
                        alignItems="center"
                        px={2}
                        py={1}
                        borderBottom="1px solid #e0e0e0"
                    >
                        <SkeletonTable columns={skeletonColumns} rows={1} />
                    </Box>
                );
            }

            if (data.length === 0) {
                return (
                    <Box style={style} display="flex" justifyContent="center" alignItems="center" px={2} py={1}>
                        <Typography color="text.secondary">{translate('organization:NO_LABS_FOUND')}</Typography>
                    </Box>
                );
            }

            const item = data[index];
            const lab = item.lab;

            return (
                <Box
                    style={style}
                    display="flex"
                    alignItems="center"
                    px={2}
                    py={1}
                    borderBottom="1px solid #e0e0e0"
                    sx={{
                        '&:hover': {
                            backgroundColor: '#fafafa',
                        },
                    }}
                >
                    <Box flex={3} display="flex" flexDirection="column" pr={2}>
                        <Typography variant="body2">{lab.name}</Typography>
                        <Typography variant="body2">{lab.organizationNumber}</Typography>
                    </Box>

                    <Box flex={3} pr={2}>
                        <Box
                            sx={{
                                lineHeight: 1.6,
                                whiteSpace: 'pre-line',
                                fontSize: '0.875rem',
                            }}
                        >
                            <FormattedAddress address={lab.contact?.address} />
                        </Box>
                    </Box>

                    <Box flex={2} display="flex" justifyContent="center">
                        <Button
                            size="small"
                            disabled={item.isAlreadyConnected || isAnyLabConnecting}
                            onClick={() => handleConnect(lab._id)}
                        >
                            {item.isAlreadyConnected
                                ? translate('organization:ALREADY_CONNECTED')
                                : isConnectingLab(lab._id)
                                  ? translate('organization:LOADING')
                                  : translate('organization:CONNECT')}
                        </Button>
                    </Box>
                </Box>
            );
        },
        [isLoadingAllLabs, translate, isConnectingLab, isAnyLabConnecting, handleConnect],
    );

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            title={translate('organization:CONNECT_TO_LAB')}
            maxWidth="lg"
            fullWidth
        >
            <Box sx={{ mb: 2 }}>
                <FreeTextFilterComponent
                    freeTextFilterChange={handleFreeTextChange}
                    label="SEARCH LAB ORG NUMBER"
                    fullWidth={false}
                    value={allLabsSearchText}
                />
            </Box>

            <Box>
                <Box display="flex" fontWeight="bold" px={2} py={1} bgcolor="#f5f5f5">
                    <Box flex={3}>
                        <TableSortLabel active direction={allLabsSortOrder} onClick={handleSort}>
                            {translate('common:LAB')}
                        </TableSortLabel>
                    </Box>
                    <Box flex={2}>{translate('invoices:DELIVERY_ADDRESS')}</Box>
                    <Box flex={3} />
                </Box>

                <List
                    height={400}
                    width="100%"
                    itemSize={100}
                    itemCount={isLoadingAllLabs ? 3 : allLabs.length === 0 ? 1 : allLabs.length}
                    itemData={allLabs}
                >
                    {Row}
                </List>
            </Box>
        </DHDialog>
    );
};

export default ConnectLabsDialog;
